import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { FiAlertTriangle, FiEdit2, FiEye } from "react-icons/fi";
import { MdDeleteForever } from "react-icons/md";
import { toast } from "react-toastify";

import SearchFilter from "../../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../../Common/ColumnVisibilityControl";
import ActionButton from "../../../../Common/ActionButton";
import StatusChip from "../../../../Common/StatusChip";
import DeleteConfirmModal from "../../../../../Common/DeleteConfirmModal";
import { quoteChipType } from "../Quotes/quotesService";
import QuotesComparisonDetailsModal from "./QuotesComparisonDetailsModal";
import { getComparisons, getComparisonById, deleteComparison } from "./quotesComparisonService";

/**
 * Quotes Comparison Table
 * Route: /procurement/quotations/comparison
 */
const QuotesComparisonTable = () => {
  const history = useHistory();

  const gridRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [columnVisibility, setColumnVisibility] = useState({});
  const [comparisons, setComparisons] = useState([]);
  const [selectedComparison, setSelectedComparison] = useState(null);
  const [viewComparison, setViewComparison] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load comparisons from service
  const loadComparisonsData = useCallback(() => {
    getComparisons()
      .then(setComparisons)
      .catch((err) => toast.error(err.message || "Failed to load comparisons"));
  }, []);

  useEffect(() => {
    loadComparisonsData();
  }, [loadComparisonsData]);

  // Client-side quick filter
  const filteredComparisons = useMemo(() => {
    if (!searchTerm) {
      return comparisons;
    }
    const lower = searchTerm.toLowerCase();
    return comparisons.filter((item) =>
      [
        item?.CSNumber || "",
        item?.ProjectName || "",
        item?.RequisitionNo || "",
        item?.Status || "",
        item?.CSDate || "",
        ...(item?.AwardedVendors || []),
        ...(item?.Vendors || []).map((v) => v?.VendorName || ""),
      ].some((val) => String(val).toLowerCase().includes(lower)),
    );
  }, [comparisons, searchTerm]);

  // Ensure row heights adjust dynamically when data is loaded/filtered
  useEffect(() => {
    if (gridRef.current?.api) {
      const timer = setTimeout(() => {
        gridRef.current?.api?.resetRowHeights();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [filteredComparisons]);

  // Handle delete
  const handleConfirmDelete = async () => {
    if (!selectedComparison) {
      return;
    }
    setIsDeleting(true);
    try {
      const updated = await deleteComparison(selectedComparison.Id);
      setComparisons(updated);
      toast.success(`Comparison "${selectedComparison.CSNumber}" deleted successfully`);
    } catch (err) {
      toast.error(err.message || "Failed to delete comparison");
    } finally {
      setIsDeleting(false);
      setSelectedComparison(null);
    }
  };

  // List rows carry no matrix, so the details come from the comparison itself.
  const openDetails = useCallback(async (row) => {
    try {
      setViewComparison(await getComparisonById(row.Id));
    } catch (err) {
      toast.error(err.message || "Failed to load comparison");
    }
  }, []);

  const addPath = "/procurement/quotations/comparison/add";

  /* ─── Column Definitions ──────────────────────────────────────────────── */
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        filter: false,
        sortable: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "CS Number",
        field: "CSNumber",
        flex: 1.1,
        minWidth: 140,
        cellRenderer: (params) => (
          <span style={{ fontWeight: 600, color: "#056363" }}>{params.value}</span>
        ),
      },
      {
        headerName: "Project / Site",
        field: "ProjectName",
        flex: 1.2,
        minWidth: 140,
      },
      {
        headerName: "Linked Requisition",
        field: "RequisitionNo",
        flex: 1.2,
        minWidth: 140,
        cellRenderer: (params) =>
          params.value ? (
            <span
              className="badge"
              style={{
                backgroundColor: "#e0f2fe",
                color: "#0369a1",
                fontSize: "12px",
                padding: "4px 8px",
              }}
            >
              {params.value}
            </span>
          ) : (
            "—"
          ),
      },
      {
        headerName: "CS Date",
        field: "CSDate",
        flex: 1,
        minWidth: 110,
      },
      {
        headerName: "Compared Vendors",
        colId: "vendorsList",
        flex: 2,
        minWidth: 260,
        autoHeight: true,
        wrapText: true,
        cellStyle: {
          whiteSpace: "normal",
        },
        cellRenderer: (params) => {
          const vendors = params.data?.Vendors || [];
          return (
            <div
              className="py-1"
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "5px",
                width: "100%",
              }}
            >
              {vendors.map((v, idx) => (
                <span
                  key={idx}
                  className="badge badge-light border"
                  style={{
                    fontSize: "11px",
                    color: "#374151",
                    whiteSpace: "nowrap",
                    padding: "3px 6px",
                  }}
                >
                  {v.VendorName}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        headerName: "Awarded Bidder(s)",
        colId: "awardedVendors",
        flex: 1.6,
        minWidth: 220,
        autoHeight: true,
        wrapText: true,
        cellStyle: {
          whiteSpace: "normal",
        },
        cellRenderer: (params) => {
          const awarded = (params.data?.Vendors || []).filter((v) => v.Awarded);
          if (awarded.length === 0) {
            return (
              <span className="text-muted" style={{ fontStyle: "italic", fontSize: "12px" }}>
                Pending Award
              </span>
            );
          }
          return (
            <div
              className="py-1"
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "5px",
                width: "100%",
              }}
            >
              {awarded.map((v, idx) => (
                <span
                  key={idx}
                  className="badge"
                  style={{
                    backgroundColor: "#dcfce7",
                    color: "#166534",
                    border: "1px solid #bbf7d0",
                    fontSize: "11px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    padding: "3px 6px",
                  }}
                >
                  ✓ {v.VendorName}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        headerName: "Status",
        field: "Status",
        flex: 1,
        minWidth: 130,
        cellRenderer: (params) => {
          const status = params.value || "Pending Approval";
          return (
            <div className="d-flex align-items-center" style={{ gap: "6px" }}>
              <StatusChip status={status} type={quoteChipType(status)} />
              {params.data?.IsBlocked && (
                <FiAlertTriangle
                  size="1.1em"
                  color="#b06b00"
                  title={params.data.BlockedReason || "This comparison cannot be approved"}
                />
              )}
            </div>
          );
        },
      },
      {
        headerName: "Actions",
        colId: "actions",
        width: 130,
        pinned: "right",
        lockPosition: "right",
        filter: false,
        sortable: false,
        suppressMenu: true,
        headerComponent: () => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <ActionButton text="New+" newBtn="true" onClick={() => history.push(addPath)} />
          </div>
        ),
        cellRenderer: (params) => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              gap: "12px",
            }}
          >
            <FiEye
              title="View Rate Matrix"
              size="1.15em"
              style={{ cursor: "pointer", color: "#0284c7" }}
              onClick={(e) => {
                e.stopPropagation();
                openDetails(params.data);
              }}
            />
            {/* Edit only while awaiting the first decision; delete also when rejected or stuck */}
            {params.data?.Status === "Pending Approval" && !params.data?.HasDecision && (
              <FiEdit2
                title="Edit Comparison"
                size="1.1em"
                style={{ cursor: "pointer", color: "#4e545a" }}
                onClick={(e) => {
                  e.stopPropagation();
                  history.push(addPath, { Id: params.data.Id, isEdit: true });
                }}
              />
            )}
            {(params.data?.Status === "Rejected" ||
              (params.data?.Status === "Pending Approval" &&
                (!params.data?.HasDecision || params.data?.IsBlocked))) && (
              <MdDeleteForever
                title="Delete"
                size="1.2em"
                className="cur_sor"
                style={{ cursor: "pointer", color: "#c0392b" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedComparison(params.data);
                }}
              />
            )}
          </div>
        ),
      },
    ],
    [history, addPath, openDetails],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (p) => p.value,
      valueFormatter: (p) =>
        p.value === null || p.value === undefined || p.value === "" ? "N/A" : p.value,
      minWidth: 90,
      cellClass: (params) => (params.colDef?.autoHeight ? "" : "d-flex align-items-center"),
    }),
    [],
  );

  /* ─── Column show / hide ──────────────────────────────────────────────── */
  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
    setTimeout(() => {
      params.api?.resetRowHeights();
    }, 100);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({ ...prev, [colId]: !isCurrentlyVisible }));
    },
    [columnVisibility],
  );

  /* ─── Render ──────────────────────────────────────────────────────────── */
  return (
    <>
      {/* Toolbar */}
      <div
        className="d-flex flex-wrap align-items-center justify-content-end pt-3 app_font w-100"
        style={{ gap: "12px", width: "100%" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            minHeight: "30px",
            maxWidth: "100%",
            marginRight: "auto",
            padding: "4px 12px",
            color: "var(--btn-comp-text, #0b6462)",
            fontSize: "var(--font-md)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
          title="Quotes Comparison"
        >
          Quotes Comparison
        </div>
        <SearchFilter
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search Comparisons..."
        />

        <ColumnVisibilityControl
          gridApi={gridRef.current?.api}
          columnVisibility={columnVisibility}
          toggleColumn={toggleColumn}
        />
      </div>

      {/* Grid */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div className="ag-theme-alpine vouchers_table_height3 mt-3" style={{ width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredComparisons || []}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              quickFilterText={searchTerm}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
              onRowClicked={(e) => {
                if (!e.event.target.closest("button, svg")) {
                  openDetails(e.data);
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        open={!!selectedComparison}
        onClose={() => setSelectedComparison(null)}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        message={`Are you sure you want to delete Comparative Statement "${selectedComparison?.CSNumber}"?`}
        heading="Delete Comparison"
      />

      {/* View Matrix Modal */}
      <QuotesComparisonDetailsModal
        open={!!viewComparison}
        onClose={() => setViewComparison(null)}
        comparison={viewComparison}
        onChanged={async () => {
          loadComparisonsData();
          await openDetails(viewComparison);
        }}
      />
    </>
  );
};

export { QuotesComparisonTable };
export default QuotesComparisonTable;
